export const CONNECTION_STATUS = {
  INCOMPLETE: "INCOMPLETE",
  PENDING: "PENDING",
  LINK_EXPIRED: "LINK_EXPIRED",
  FORWARDING_REQUIRED: "FORWARDING_REQUIRED",
  FORWARDING_EXISTING: "FORWARDING_EXISTING",
  CONFIRMING_FORWARDING: "CONFIRMING_FORWARDING",
  FORWARDING_CONFIRMED: "FORWARDING_CONFIRMED",
  FORWARDING_FILTER_CONFIRMED: "FORWARDING_FILTER_CONFIRMED",
  UPDATE_FILTER: "UPDATE_FILTER",
  VERIFYING: "VERIFYING",
  SUB_BRANDS_PENDING: "SUB_BRANDS_PENDING",
  CONNECTED: "CONNECTED",
  VERIFICATION_FAILED: "VERIFICATION_FAILED",
  FORWARDING_VERIFICATION_FAILED: "FORWARDING_VERIFICATION_FAILED",
  NOT_RECEIVING_DATA: "NOT_RECEIVING_DATA",
} as const;

export type ConnectionStatus =
  (typeof CONNECTION_STATUS)[keyof typeof CONNECTION_STATUS];

export const BADGE_ONLY_STATUS = {
  NOT_CONNECTED: "NOT_CONNECTED",
  EXPIRED: "EXPIRED",
} as const;

export type BadgeOnlyStatus =
  (typeof BADGE_ONLY_STATUS)[keyof typeof BADGE_ONLY_STATUS];
export type ChannelBadgeStatus = ConnectionStatus | BadgeOnlyStatus;
export type ConnectionStatusVariant = "grey" | "amber" | "red" | "green";

const VERIFICATION_STATUSES = new Set<ChannelBadgeStatus>([
  CONNECTION_STATUS.VERIFYING,
  CONNECTION_STATUS.SUB_BRANDS_PENDING,
  CONNECTION_STATUS.CONNECTED,
  CONNECTION_STATUS.VERIFICATION_FAILED,
  CONNECTION_STATUS.NOT_RECEIVING_DATA,
]);

/** Verification has started for this connection, whether it is still running, done or failed. */
export const isVerificationStatus = (status: ChannelBadgeStatus): boolean =>
  VERIFICATION_STATUSES.has(status);

export const isVerifying = (status: ChannelBadgeStatus): boolean =>
  status === CONNECTION_STATUS.VERIFYING;

/** Verification failed: the channel is fixed through the credentials page and the recheck steps. */
export const isFailedStatus = (status: ChannelBadgeStatus): boolean =>
  status === CONNECTION_STATUS.VERIFICATION_FAILED || status === CONNECTION_STATUS.NOT_RECEIVING_DATA;

export interface ConnectionStatusPresentation {
  label: string;
  variant: ConnectionStatusVariant;
  spinner: boolean;
}

const amber = (label: string, spinner = false): ConnectionStatusPresentation => ({
  label,
  variant: "amber",
  spinner,
});
const red = (label: string): ConnectionStatusPresentation => ({ label, variant: "red", spinner: false });

export const CONNECTION_STATUS_PRESENTATION: Record<ChannelBadgeStatus, ConnectionStatusPresentation> = {
  INCOMPLETE: amber("Incomplete"),
  PENDING: amber("Pending"),
  LINK_EXPIRED: red("Link Expired"),
  FORWARDING_REQUIRED: amber("Forwarding Required"),
  FORWARDING_EXISTING: amber("Check forwarding"),
  CONFIRMING_FORWARDING: amber("Confirming forwarding", true),
  FORWARDING_CONFIRMED: amber("Forwarding Confirmed"),
  FORWARDING_FILTER_CONFIRMED: amber("Filter confirmed"),
  UPDATE_FILTER: amber("Update filter"),
  VERIFYING: amber("Verifying", true),
  SUB_BRANDS_PENDING: amber("Sub-Brands Pending"),
  CONNECTED: { label: "Connected", variant: "green", spinner: false },
  VERIFICATION_FAILED: red("Verification failed"),
  FORWARDING_VERIFICATION_FAILED: red("Forwarding failed"),
  NOT_RECEIVING_DATA: red("Not receiving data"),
  NOT_CONNECTED: { label: "Not connected", variant: "grey", spinner: false },
  EXPIRED: red("Expired"),
};

export const CONNECTION_STATUS_LABELS: Record<ChannelBadgeStatus, string> =
  Object.fromEntries(
    Object.entries(CONNECTION_STATUS_PRESENTATION).map(([status, value]) => [status, value.label])
  ) as Record<ChannelBadgeStatus, string>;
